import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MfaChallenge from "../auth/MfaChallenge";
import AuthErrorPage from "../components/AuthErrorPage";
import { LoaderMessage } from "../components/LoaderMessage";
import { getAuthConfig } from "../config/AuthConfigContext";
import { useFacilityData } from "../hooks/useFacilityData";
import { useInactivityLogout } from "../hooks/useInactivityLogout";
import { useRbacToken } from "../hooks/useRbacToken";
import { useAuthDispatch, useAuthSelector } from "../state/AuthStoreProvider";
import { fetchAllFormOptionsFromFirebase } from "../state/formOptionsSlice";
import {
  getProviderDetailsFor,
  PROVIDER_DETAILS_NOT_FOUND_ERROR,
} from "../state/providerDetailsSlice";
import { fetchSingleHealthFacilityDetailsFromFirebase } from "../state/singleHealthFacilitySlice";
import type { AuthRootState } from "../state/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/userGeneralInfoStore";

interface ProtectRouteProps {
  /** The protected content rendered once the user is fully authenticated and provisioned. */
  children: React.ReactNode;
}

/**
 * Check whether a route is inside the configured facility recovery area.
 *
 * @param pathname - Current browser route
 * @param recoveryPath - Configured Settings recovery route
 * @returns Whether the current route may render during a facility-level 403
 */
function isFacilityRecoveryRoute(
  pathname: string,
  recoveryPath: string | undefined,
): boolean {
  if (!recoveryPath) return false;
  const normalizedPath = recoveryPath.endsWith("/")
    ? recoveryPath.slice(0, -1)
    : recoveryPath;
  return (
    pathname === normalizedPath || pathname.startsWith(`${normalizedPath}/`)
  );
}

/**
 * Route guard that gates its children behind authentication and provisioning checks.
 *
 * Verifies the Firebase user, loads provider and facility details, and enforces
 * MFA and RBAC. Renders {@link AuthErrorPage} when the user has no facility
 * assigned or their provider record is missing
 * ({@link PROVIDER_DETAILS_NOT_FOUND_ERROR}), an MFA challenge when required, and
 * a loader until all data is ready.
 *
 * @param props - Component props
 * @param props.children - Content to render once access is granted
 * @returns The children when authorized, otherwise an error page, MFA challenge, or loader
 */
export default function ProtectRoute({ children }: ProtectRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAuthDispatch();

  const hasBeenReady = useRef(false);
  const hasDispatchedCurrentUser = useRef(false);
  const hasFetchedProviderDetails = useRef(false);

  const { user, verifyingUser } = useAuthSelector(
    (state: AuthRootState) => state.firebaseAuthentication,
  );
  const {
    data: providerDetails,
    loading: isProviderDetailsLoading,
    error: providerDetailsNotFoundError,
  } = useAuthSelector((state: AuthRootState) => state.providerDetails);
  const isProviderDetailsNotFoundError =
    providerDetailsNotFoundError === PROVIDER_DETAILS_NOT_FOUND_ERROR;

  useInactivityLogout();
  const {
    isLoaded: isRbacTokenLoaded,
    hasNoFacilityAssigned: rbacHasNoFacility,
    authError,
    mfaStatus,
  } = useRbacToken(providerDetails?.FacilityId);
  const hasNoFacilityAssigned =
    rbacHasNoFacility ||
    (!isProviderDetailsLoading &&
      providerDetails &&
      (!providerDetails.FacilityId ||
        providerDetails.FacilityId.trim() === ""));

  const {
    data: singleHealthFacilityDetails,
    loading: isSingleHealthFacilityDetailsLoading,
  } = useAuthSelector(
    (state: AuthRootState) => state.singleHealthFacilityDetails,
  );

  useFacilityData(isRbacTokenLoaded, providerDetails?.FacilityId);

  const checkIfAllDataExistsInContext = () => {
    const userId = UserStore.getItem(UserGeneralInfoContextKeys.USER_ID);
    const healthcareFacilityId = UserStore.getItem(
      UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID,
    );
    return !!(userId && healthcareFacilityId);
  };

  useEffect(() => {
    if (
      hasDispatchedCurrentUser.current ||
      verifyingUser !== "verified" ||
      !user?.id ||
      !isRbacTokenLoaded
    ) {
      return;
    }
    hasDispatchedCurrentUser.current = true;
    dispatch(fetchAllFormOptionsFromFirebase());
  }, [dispatch, isRbacTokenLoaded, user?.id, verifyingUser]);

  useEffect(() => {
    if (user?.id) {
      UserStore.setItem(UserGeneralInfoContextKeys.USER_ID, user.id);
    }
  }, [user?.id]);

  useEffect(() => {
    if (singleHealthFacilityDetails) {
      UserStore.setItem(
        UserGeneralInfoContextKeys.ENABLE_REVIEW_MODE,
        singleHealthFacilityDetails.feature_config?.enable_review_mode === true,
      );
      if (
        singleHealthFacilityDetails?.is_external_organization &&
        singleHealthFacilityDetails?.corresponding_internal_org_id
      ) {
        UserStore.setItem(
          UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
          singleHealthFacilityDetails.corresponding_internal_org_id,
        );
      }
    }
  }, [singleHealthFacilityDetails]);

  useEffect(() => {
    if (verifyingUser === "not-verified") {
      const config = getAuthConfig();
      navigate(config?.authRedirectPath ?? "/auth");
      return;
    }

    if (
      user &&
      providerDetails?.id !== user.id &&
      !hasFetchedProviderDetails.current
    ) {
      hasFetchedProviderDetails.current = true;
      dispatch(getProviderDetailsFor(user.id));
    }

    if (providerDetails?.FacilityId && hasFetchedProviderDetails.current) {
      dispatch(
        fetchSingleHealthFacilityDetailsFromFirebase(
          providerDetails.FacilityId,
        ),
      );
    }
  }, [
    user?.id,
    verifyingUser,
    providerDetails?.id,
    providerDetails?.FacilityId,
  ]);

  if (verifyingUser === "verified" && user && mfaStatus) {
    return <MfaChallenge />;
  }

  const config = getAuthConfig();
  const isForbiddenFacilityRecovery =
    authError?.type === "forbidden" &&
    isFacilityRecoveryRoute(
      location.pathname,
      config?.facilityRecoverySettingsPath,
    );

  if (verifyingUser === "verified" && user && isForbiddenFacilityRecovery) {
    return <>{children}</>;
  }

  if (verifyingUser === "verified" && user && authError) {
    return (
      <AuthErrorPage
        title={authError.title}
        message={authError.message}
        showRefreshButton={false}
        settingsRecoveryPath={
          authError.type === "forbidden"
            ? config?.facilityRecoverySettingsPath
            : undefined
        }
      />
    );
  }

  if (
    verifyingUser === "verified" &&
    user &&
    (hasNoFacilityAssigned || isProviderDetailsNotFoundError)
  ) {
    return <AuthErrorPage />;
  }

  const isReady =
    verifyingUser === "verified" &&
    user &&
    checkIfAllDataExistsInContext() &&
    !isProviderDetailsLoading &&
    !isSingleHealthFacilityDetailsLoading &&
    isRbacTokenLoaded;

  if (isReady) {
    hasBeenReady.current = true;
  }

  if (hasBeenReady.current) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderMessage message="Verifying user authentication ..." />
    </div>
  );
}
